import { useEffect, useMemo } from 'react';
import { useSessionStore } from '@/entities/session';
import { useUserScopeConfiguration } from '@/entities/user';
import {
  ACCOUNTS_SECTION_TYPES,
  type AccountType,
  type ActionButtonsVisibility,
} from './types';
import { useAccountActionsAvailability } from './useAccountActionsAvailabilityStore';
import { useAssetTransferAvailability } from './useAssetTransferAvailability';
import { useAccountsStore } from './useAccountsStore';

type UseAccountActionsVisibilityResult = {
  visibility: ActionButtonsVisibility;
};

function useAccountActionsVisibility(): UseAccountActionsVisibilityResult {
  const { session } = useSessionStore();
  const isUserVerified = session?.additional?.isVerified || false;

  const { setAccountActionsAvailability } = useAccountActionsAvailability();
  const { getAccountTypeTransferAvailability } = useAssetTransferAvailability();

  const {
    userScopeConfiguration,
    isUserScopeConfigurationLoading,
    userScopeConfigurationError,
  } = useUserScopeConfiguration();
  const { accountsQty } = useAccountsStore();

  const visibility: ActionButtonsVisibility = useMemo(() => {
    if (isUserScopeConfigurationLoading || userScopeConfigurationError) {
      return {
        deposit: false,
        withdraw: false,
        exchange: false,
        transfer: false,
        trade: false,
      };
    }

    const atLeastOneAccountTypeIsAllowedToTransfer = [
      ACCOUNTS_SECTION_TYPES.ewallet,
      ACCOUNTS_SECTION_TYPES.spot,
      ACCOUNTS_SECTION_TYPES.invest,
      ACCOUNTS_SECTION_TYPES.leverage,
    ].some(accountType =>
      getAccountTypeTransferAvailability(accountType as AccountType)
    );

    return {
      deposit: true,
      withdraw: true,
      exchange: Boolean(userScopeConfiguration?.exchange.exchange),
      transfer:
        atLeastOneAccountTypeIsAllowedToTransfer &&
        Boolean(userScopeConfiguration?.transfer.transfer),
      trade: true,
    };
  }, [
    accountsQty,
    getAccountTypeTransferAvailability,
    isUserVerified,
    userScopeConfiguration,
    isUserScopeConfigurationLoading,
    userScopeConfigurationError,
  ]);

  useEffect(() => {
    setAccountActionsAvailability(visibility);
  }, [setAccountActionsAvailability, visibility]);

  return { visibility };
}

export { useAccountActionsVisibility };
